import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { MdClose } from "react-icons/md";

import { getLanguage } from "../../../utils/getLanguage";
import Button from "../../Button";
import CustomDataTable from "../../CustomDataTable";
import Modal from "../../Modal";
import CompleteLastLessonModal from "./CompleteLastLessonModal";
import ConfirmUnlockLockLessonsModal from "./ConfirmUnlockLockLessonsModal";

const UnlockLessonsModal = forwardRef(({ onSubmit, getClassroomDetails }, ref) => {
  const { t } = useTranslation();
  const lang = getLanguage();

  const [isOpen, setIsOpen] = useState(false);
  const [classroom, setClassroom] = useState(null);
  const [lessons, setLessons] = useState([]);
  const [first, setFirst] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(7);

  const confirmModalRef = useRef();
  const completeLastModalRef = useRef();
  const lessonRefs = useRef({});

  useImperativeHandle(ref, () => ({
    openModal(classroomData, lessonsData) {
      setClassroom(classroomData);
      const filteredLessons = lessonsData.filter((lesson) => lesson.is_ordered !== 0);
      setLessons(filteredLessons);

      const lastLesson = filteredLessons[filteredLessons.length - 1];
      const lastLessonUnlocked = lastLesson && lastLesson.is_locked === 0;

      if (lastLessonUnlocked) {
        const lastPageFirstIndex = Math.floor((filteredLessons.length - 1) / rowsPerPage) * rowsPerPage;
        setFirst(lastPageFirstIndex);
      } else {
        const firstUnlockIndex = filteredLessons.findIndex((lesson, idx) => {
          const prev = idx > 0 ? filteredLessons[idx - 1] : null;
          return lesson.is_locked === 1 && (!prev || prev.is_locked === 0 || prev.finished_by_teacher);
        });
        setFirst(firstUnlockIndex !== -1 ? Math.floor(firstUnlockIndex / rowsPerPage) * rowsPerPage : 0);
      }

      setIsOpen(true);
    },

    closeModal: handleCloseModal,
  }));

  const handleCloseModal = () => {
    setIsOpen(false);
    setRowsPerPage(7);
  };

  const handleConfirmSuccess = () => {
    onSubmit();
    handleCloseModal();
  };

  const handleCompleteLastLessonSuccess = () => {
    onSubmit();
    handleCloseModal();
  };

  const handleUnlockClick = (lesson) => {
    const index = lessons.findIndex((l) => l.ordering === lesson.ordering);
    const prev = index > 0 ? lessons[index - 1] : null;

    if (confirmModalRef.current) {
      confirmModalRef.current.openModal(classroom, lesson, prev);
    }
  };

  const handleCompleteLastLesson = (lesson) => {
    if (completeLastModalRef.current) {
      completeLastModalRef.current.openModal(classroom, lesson);
    }
  };

  const handleClickIncompleteLesson = (lesson) => {
    const index = lessons.findIndex((l) => l.ordering === lesson.ordering);
    console.log(index);

    const prev = index > 0 ? lessons[index - 1] : lesson;
    if (prev && confirmModalRef.current) {
      confirmModalRef.current.openModal(classroom, lesson, prev);
    }
  };

  const isLastCompleted = (lesson) => {
    const lastCompletedId = [...lessons].reverse().find((l) => l.finished_by_teacher)?.lesson_id;
    return lesson.finished_by_teacher && lesson.lesson_id === lastCompletedId;
  };

  const isLastLessonUnlocked = () => {
    if (lessons.length === 0) return false;
    const lastLesson = lessons[lessons.length - 1];
    return lastLesson.is_locked === 0;
  };

  const renderLessonTitle = (lesson) => {
    const title = lang === "ar" ? lesson.title_ar : lesson.title_en;
    return (
      <div ref={(el) => (lessonRefs.current[lesson.lesson_id] = el)} className="flex items-center gap-2">
        <span
          title={title}
          className="block w-80 truncate whitespace-nowrap text-start font-medium text-blue-500"
        >
          {title}
        </span>
      </div>
    );
  };

  const renderLessonStatus = (lesson) => {
    const index = lessons.findIndex((l) => l.ordering === lesson.ordering);
    const prev = index > 0 ? lessons[index - 1] : null;

    const canUnlock = lesson.is_locked === 1 && (!prev || prev.is_locked === 0 || prev.finished_by_teacher);

    return (
      <div className="relative flex items-center">
        {isLastCompleted(lesson) && (
          <Button
            className={`absolute top-[-16px] ${lang === "ar" ? "right-[-45px]" : "left-[-45px]"}`}
            variant="text"
            color="yellow"
            icon={<MdClose size={24} />}
            tooltip={t("confirm_lock")}
            onClick={() => handleClickIncompleteLesson(lesson)}
          />
        )}
        {lesson.finished_by_teacher ? (
          <span className="text-yellow-500">{t("completed")}</span>
        ) : isLastLessonUnlocked() ? (
          <Button label={t("complete_last_lesson")} onClick={() => handleCompleteLastLesson(lesson)} />
        ) : lesson.is_locked === 1 ? (
          canUnlock ? (
            <Button label={t("unlock")} onClick={() => handleUnlockClick(lesson)} />
          ) : (
            <span>{t("locked")}</span>
          )
        ) : (
          <span>{t("unlocked")}</span>
        )}
      </div>
    );
  };

  const columns = [
    {
      name: (
        <div className="flex items-center space-x-2">
          <span className="text-xl">{t("lesson_name")}</span>
        </div>
      ),
      field: lang === "ar" ? "title_ar" : "title_en",
      body: renderLessonTitle,
    },
    {
      name: (
        <div className="flex items-center space-x-2">
          <span className="text-xl">{t("status")}</span>
        </div>
      ),
      field: "is_locked",
      body: renderLessonStatus,
    },
  ];

  return (
    <>
      <Modal
        isOpen={isOpen}
        onClose={handleCloseModal}
        className="w-full max-w-2xl px-0"
        contentClassname="p-0"
        header={<h3 className="text-2xl font-bold text-blue-500">{t("unlock_lessons")}</h3>}
        footer={
          <div className="flex items-center justify-end space-x-2">
            <Button color="white" variant="outlined" onClick={handleCloseModal} label={t("cancel")} />
          </div>
        }
      >
        <CustomDataTable
          header={false}
          columns={columns}
          data={lessons}
          withPagination
          withSort
          first={first}
        />
      </Modal>

      <ConfirmUnlockLockLessonsModal
        ref={confirmModalRef}
        onSubmit={handleConfirmSuccess}
        getClassroomDetails={getClassroomDetails}
      />

      <CompleteLastLessonModal
        ref={completeLastModalRef}
        onSubmit={handleCompleteLastLessonSuccess}
        getClassroomDetails={getClassroomDetails}
      />
    </>
  );
});

export default UnlockLessonsModal;
